import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Calendar,
  Users,
  FileText,
  User,
  ArrowLeft,
  Menu,
} from "lucide-react";
// import { contractsData } from '../data/contracts.js';
import { baseURL } from "../network/baseUrl.js";
import { getProtected } from "../network/ApiService.js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { API_END_POINTS } from "../network/apiEndPoint.js";
import HeroSection from "./HeroSection/HeroSection.js";
import { useTranslation } from "react-i18next";
import defaultImage from "../assets/photo.png";
import { getAttachmentThumbUrl } from "../utils/attachmentImages";
import SelectField from "../SharedComponent/Fields/SelectField.js";
import { FaChevronDown } from "react-icons/fa";

const GANTT_HEADER_MONTH_H = "h-[37px]";
const GANTT_HEADER_DAY_ABBR_H = "h-6";
const GANTT_HEADER_DAY_NUM_H = "h-8";
const GANTT_ROW_H = "h-10";

const GanttChart = () => {
  const [selectedContract, setSelectedContract] = useState(null);
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [contractsData, setContractsData] = useState([]);
  const [visibleContracts, setVisibleContracts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const { t } = useTranslation();
  const timelineRef = useRef(null);
  const [warehouses, setWarehouses] = useState([]);
  const [hasScrolledToCurrentMonth, setHasScrolledToCurrentMonth] =
    useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState({
    classification: false,
  });
  const [selectedClassification, setSelectedClassification] = useState("");
  const assetImages = [defaultImage];
  const [loading, setLoading] = useState(false);
  const classificationOptions = [
    // {
    //   value: "",
    //   option: t("assets.fields.selectClassification"),
    // },
    { value: "Other", option: "Other" },
    {
      value: "Rough-terrain-forklift",
      option: "Rough-terrain-forklift",
    },
    {
      value: "Container-Stacker",
      option: "Container-Stacker",
    },
    { value: "Reach-stacker", option: "Reach-stacker" },
    {
      value: "Terminal tractor",
      option: "Terminal tractor",
    },
    { value: "Tractor", option: "Tractor" },
    { value: "Trailer", option: "Trailer" },
    {
      value: "Electric platform trucks",
      option: "Electric platform trucks",
    },
    {
      value: "Electric 3-wheel",
      option: "Electric 3-wheel",
    },
    {
      value: "Electric 4-wheel",
      option: "Electric 4-wheel",
    },
    {
      value: "Diesel Forklift",
      option: "Diesel Forklift",
    },
    {
      value: "Compact Forklift Truck",
      option: "Compact Forklift Truck",
    },
    { value: "LPG Forklift", option: "LPG Forklift" },
    { value: "Pallet truck", option: "Pallet truck" },
    {
      value: "Pallet Trucks Sit-down",
      option: "Pallet Trucks Sit-down",
    },
    {
      value: "Scissor elevating truck platform",
      option: "Scissor elevating truck platform",
    },
    {
      value: "Lift trucks with scales",
      option: "Lift trucks with scales",
    },
    {
      value: "Hand pallet truck",
      option: "Hand pallet truck",
    },
    {
      value: "Horizontal order picker",
      option: "Horizontal order picker",
    },
    {
      value: "Vertical order picker",
      option: "Vertical order picker",
    },
    {
      value: "High-lift pallet truck",
      option: "High-lift pallet truck",
    },
    {
      value: "Stacker stand-on",
      option: "Stacker stand-on",
    },
    {
      value: "High lift pallet trucks sit-down",
      option: "High lift pallet trucks sit-down",
    },
    { value: "Reach truck", option: "Reach truck" },
    {
      value: "Narrow aisle truck",
      option: "Narrow aisle truck",
    },
    {
      value: "Telescopic forklift, fixed",
      option: "Telescopic forklift, fixed",
    },
    {
      value: "Telescopic forklift, rotating",
      option: "Telescopic forklift, rotating",
    },
    {
      value: "Four-way loader",
      option: "Four-way loader",
    },
    {
      value: "Four-way reachtruck",
      option: "Four-way reachtruck",
    },
    { value: "Sideloader", option: "Sideloader" },
    {
      value: "Truck mounted forklift",
      option: "Truck mounted forklift",
    },
    {
      value: "Tugger-train-trailer",
      option: "Tugger-train-trailer",
    },
    { value: "CNG-Forklift", option: "CNG-Forklift" },
    {
      value: "Gasoline-Forklift",
      option: "Gasoline-Forklift",
    },
    {
      value: "Electric stairclimber",
      option: "Electric stairclimber",
    },
    {
      value: "Counterbalance forklift",
      option: "Counterbalance forklift",
    },
  ];

  // Generate full year timeline data with day names

  const timeline = useMemo(() => {
    const months = [];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthDate = new Date(currentYear, monthIndex, 1);
      const daysInMonth = new Date(currentYear, monthIndex + 1, 0).getDate();
      const days = Array.from({ length: daysInMonth }, (_, index) => {
        const dayNumber = index + 1;
        const date = new Date(currentYear, monthIndex, dayNumber);
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const dayAbbreviation = dayNames[dayOfWeek];
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday

        return {
          number: dayNumber,
          abbreviation: dayAbbreviation,
          isWeekend,
          dayOfWeek,
          fullDate: date,
        };
      });

      months.push({
        name: `${monthNames[monthIndex]} ${currentYear}`,
        shortName: monthNames[monthIndex],
        year: currentYear,
        month: monthIndex,
        days: days,
        daysInMonth: daysInMonth,
      });
    }

    return months;
  }, [currentYear]);

  // Calculate current day position
  const getCurrentDayPosition = () => {
    const today = new Date();
    if (today.getFullYear() !== currentYear) {
      return null;
    }

    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    let position = 0;

    // Add width of all previous months
    for (let i = 0; i < currentMonth; i++) {
      const daysInMonth = new Date(currentYear, i + 1, 0).getDate();
      position += daysInMonth * 24;
    }

    // Add current day position within the month
    position += (currentDay - 1) * 24 + 12; // +12 to center the line in the day cell

    return position;
  };

  // Function to scroll to current month
  const scrollToCurrentMonth = () => {
    if (timelineRef.current && !hasScrolledToCurrentMonth) {
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentDay = today.getDate();

      // Only scroll if we're viewing the current year
      if (currentYear === today.getFullYear()) {
        // Calculate the scroll position
        let scrollPosition = 0;

        // Add width of all previous months
        for (let i = 0; i < currentMonth; i++) {
          const daysInMonth = new Date(currentYear, i + 1, 0).getDate();
          scrollPosition += daysInMonth * 24; // 24px per day
        }

        // Add current day position within the month (optional: center on current day)
        scrollPosition += (currentDay - 1) * 24;

        // Subtract some offset to show a bit of the previous month for context
        scrollPosition = Math.max(0, scrollPosition - 200);

        timelineRef.current.scrollLeft = scrollPosition;
        setHasScrolledToCurrentMonth(true);
      }
    }
  };

  useEffect(() => {
    console.log("selected warehouse id = ", selectedWarehouse);
  }, [selectedWarehouse]);
  useEffect(() => {
    console.log("selected classification = ", selectedClassification);
  }, [selectedClassification]);
  // Reset scroll flag when year changes
  useEffect(() => {
    setHasScrolledToCurrentMonth(false);
  }, [currentYear]);

  // Scroll to current month when component mounts or data loads
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      scrollToCurrentMonth();
    }, 100);

    return () => clearTimeout(timer);
  }, [assets, currentYear, hasScrolledToCurrentMonth]);

  // Calculate total days from start of year to a specific date
  const getDaysSinceYearStart = (dateString) => {
    const date = new Date(dateString);
    const yearStart = new Date(currentYear, 0, 1);
    return Math.floor((date - yearStart) / (1000 * 60 * 60 * 24));
  };

  // Calculate contract bar position and width
  const calculateContractBar = (contract) => {
    const startDate = new Date(contract.contract_start);
    const endDate = new Date(contract.contract_end);

    // Only show contracts for the current year
    if (
      startDate.getFullYear() !== currentYear &&
      endDate.getFullYear() !== currentYear
    ) {
      return { left: -1000, width: 0, visible: false };
    }

    const dayWidth = 24; // Each day is 24px wide

    // Calculate days from year start
    const startDays = getDaysSinceYearStart(contract.contract_start);
    const endDays = getDaysSinceYearStart(contract.contract_end);

    // Adjust for contracts that start before or end after the current year
    const adjustedStartDays = Math.max(0, startDays);
    const adjustedEndDays = Math.min(
      endDays,
      getDaysSinceYearStart(`${currentYear}-12-31`)
    );

    const left = adjustedStartDays * dayWidth;
    const width = (adjustedEndDays - adjustedStartDays + 1) * dayWidth;

    return { left, width, visible: width > 0 };
  };

  // Filter contracts for current year for an asset
  const getAssetVisibleContracts = (asset) => {
    if (!asset.contracts || asset.contracts.length === 0) {
      return [];
    }

    return asset.contracts.filter((contract) => {
      const startDate = new Date(contract.contract_start);
      const endDate = new Date(contract.contract_end);
      return (
        !isNaN(startDate) &&
        !isNaN(endDate) &&
        (startDate.getFullYear() === currentYear ||
          endDate.getFullYear() === currentYear)
      );
    });
  };

  // Check if asset should show warehouse status for current year
  const shouldShowWarehouseStatus = (asset) => {
    const assetCreatedDate = new Date(asset.created_at);
    const assetCreatedYear = assetCreatedDate.getFullYear();

    // Show warehouse status if:
    // 1. Asset was created in current year OR before current year
    // 2. Asset has no contracts for current year
    return (
      assetCreatedYear <= currentYear &&
      getAssetVisibleContracts(asset).length === 0
    );
  };

  // Get warehouse contract dates for an asset
  const getWarehouseContractDates = (asset) => {
    const assetCreatedDate = new Date(asset.created_at);
    const assetCreatedYear = assetCreatedDate.getFullYear();

    let startDate, endDate;

    if (assetCreatedYear === currentYear) {
      // Asset created in current year - start from creation date
      startDate = asset.created_at.split("T")[0]; // Get just the date part
      endDate = `${currentYear}-12-31`;
    } else if (assetCreatedYear < currentYear) {
      // Asset created before current year - show for entire current year
      startDate = `${currentYear}-01-01`;
      endDate = `${currentYear}-12-31`;
    }

    return { startDate, endDate };
  };

  // Get contract colors
  const getContractColor = (contractType, index) => {
    const colors = {
      rental: "bg-blue-600",
      leasing: "bg-green-600",
      sale: "bg-purple-600",
      demo: "bg-orange-600",
      maintenance: "bg-red-600",
      "In Warehouse": "bg-gray-500",
    };
    return colors[contractType] || `bg-indigo-${500 + (index % 3) * 100}`;
  };

  const navigateYear = (direction) => {
    setCurrentYear((prev) => prev + direction);
  };

  const openContractModal = (contract) => {
    console.log("selected contract ------------- > ", contract);
    setSelectedContract(contract);
  };

  const closeModal = () => {
    setSelectedContract(null);
  };

  useEffect(() => {
    if (assets && Array.isArray(assets) && assets.length > 0) {
      const allContracts = assets.flatMap((asset) => {
        if (Array.isArray(asset.contracts) && asset.contracts.length > 0) {
          return asset.contracts;
        }
        return [];
      });

      const visiblecontracts = allContracts.filter((contract) => {
        const start = new Date(contract.contract_start);
        const end = new Date(contract.contract_end);
        return (
          !isNaN(start) &&
          !isNaN(end) &&
          (start.getFullYear() === currentYear ||
            end.getFullYear() === currentYear)
        );
      });

      setContractsData(allContracts);
      setVisibleContracts(visiblecontracts);

      console.log("✅ All Contracts:", allContracts);
      console.log("✅ Visible Contracts:", visiblecontracts);
    }
  }, [assets, currentYear]);

  const handleWarehouseChange = (e) => {
    console.log("value == ", e.target.value);
    const warehouseId = e.target.value;
    console.log("warehouses = ", warehouses);
    const selectedWarehousee = warehouses.find(
      (warehouse) => warehouse?.id == warehouseId
    );
    console.log("selected warehouse = ", selectedWarehousee);

    if (selectedWarehousee) {
      setSelectedWarehouse(selectedWarehousee);
    } else {
      setSelectedWarehouse(null);
    }
  };

  const getWarehouses = async () => {
    try {
      const url = API_END_POINTS.warehouses;
      if (!url) {
        throw new Error("API endpoint not defined");
      }
      const response = await getProtected(url);
      console.log("response warehouse = ", response);
      if (Array.isArray(response) && response?.length > 0) {
        console.log("setting warehouses = ", response);
        setWarehouses(response);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t("dashboardToasters.sessionExpiration"));
        navigate("/login");
        return;
      }
      toast.error(t("dashboardToasters.genricError"));
    }
  };

  const fetchAssets = async () => {
    setLoading(true);
    try {
      let url = `${baseURL}/assets/?all_asset=true`;
      if (selectedWarehouse && selectedClassification) {
        url = `${baseURL}/assets/?all_asset=true&warehouse=${selectedWarehouse?.id}&classification=${selectedClassification}`;
      } else if (selectedWarehouse) {
        url = `${baseURL}/assets/?all_asset=true&warehouse=${selectedWarehouse?.id}`;
      } else if (selectedClassification) {
        url = `${baseURL}/assets/?all_asset=true&classification=${selectedClassification}`;
      }
      if (!url) {
        throw new Error("API endpoint not defined");
      }
      const response = await getProtected(url);
      console.log("response = ", response);
      if (Array.isArray(response) && response?.length > 0) {
        console.log("fetch assets api response = ", response);
        setAssets(response);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(t("dashboardToasters.sessionExpiration"));
        navigate("/login");
        return;
      }
      toast.error(t("dashboardToasters.genricError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWarehouses();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [selectedWarehouse, selectedClassification]);

  const currentDayPosition = getCurrentDayPosition();

  const toggleDropdown = (key) => {
    setDropdownOpen((prev) => ({
      classification: false,

      [key]: !prev[key],
    }));
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
        <div className="text-center px-4 w-full max-w-sm">
          <div className="mx-auto h-12 w-12 sm:h-14 sm:w-14 mb-4 sm:mb-6">
            <div className="animate-spin h-full w-full border-4 sm:border-8 border-gray-200 border-t-blue-500 rounded-full"></div>
          </div>
          <div className="text-white text-lg sm:text-xl font-medium break-words">
            {/* {t("detailViewModel.loadingDetailView")} */}
            {t("utilizationBoard.loadingUtilizationBoard")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      {/* Header */}
      <HeroSection t={t} />
      <div className="bg-gray-800 p-4 border-b border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className="cursor-pointer"
                onClick={() => {
                  navigate("/dashboard");
                }}
              >
                <ArrowLeft className="h-6 w-6" />
              </div>
              <h1 className="text-lg sm:text-xl font-semibold">
                {t("utilizationBoard.title")}
              </h1>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigateYear(-1)}
                  className="p-1 hover:bg-gray-700 rounded"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="text-sm font-medium min-w-[80px] text-center">
                  {currentYear}
                </span>
                <button
                  onClick={() => navigateYear(1)}
                  className="p-1 hover:bg-gray-700 rounded"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            {/* Mobile hamburger button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Menu size={20} />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:items-center">
            {Array.isArray(warehouses) && warehouses?.length > 0 && (
              <div className="w-full sm:w-auto lg:w-48 z-[40]">
                <div className="relative w-full">
                  <button
                    type="button"
                    className="
          flex items-center justify-between 
          w-full px-4 py-3
          bg-gray-800
          border border-gray-600
          rounded-lg
          text-gray-100
          text-sm
          shadow-sm
          transition-all
          duration-200
          ease-in-out
          hover:bg-gray-750
          hover:border-gray-500
          focus:outline-none
          focus:ring-2
          focus:ring-blue-500
          focus:border-blue-500
        "
                    onClick={() => toggleDropdown("warehouse")}
                  >
                    <span className="truncate">
                      {selectedWarehouse?.name ||
                        t("createAsset.placeholders.warehousePlaceholder")}
                    </span>
                    <svg
                      className={`ml-2 w-4 h-4 text-gray-400 transition-transform duration-200 ${
                        dropdownOpen.warehouse ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {dropdownOpen.warehouse && (
                    <div
                      className="
          absolute z-10 w-full mt-1
          bg-gray-800
          border border-gray-600
          rounded-lg
          shadow-lg
          max-h-96
          overflow-y-auto
          backdrop-blur-sm
        "
                    >
                      <ul className="py-1">
                        {/* Default/Placeholder option */}
                        <li
                          className="
                px-4 py-2
                text-sm text-gray-100
                hover:bg-gray-700
                hover:text-white
                cursor-pointer
                transition-colors
                duration-150
                ease-in-out
              "
                          onClick={() => {
                            handleWarehouseChange({ target: { value: "" } });
                            setDropdownOpen((prev) => ({
                              ...prev,
                              warehouse: false,
                            }));
                          }}
                        >
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full mr-3 bg-gray-500"></div>
                            {t("createAsset.placeholders.warehousePlaceholder")}
                          </div>
                        </li>

                        {/* Warehouse options */}
                        {warehouses.map((warehouse, index) => (
                          <li
                            key={warehouse.id}
                            className="
                  px-4 py-2
                  text-sm text-gray-100
                  hover:bg-gray-700
                  hover:text-white
                  cursor-pointer
                  transition-colors
                  duration-150
                  ease-in-out
                  border-t border-gray-700/50
                "
                            onClick={() => {
                              handleWarehouseChange({
                                target: { value: warehouse.id },
                              });
                              setDropdownOpen((prev) => ({
                                ...prev,
                                warehouse: false,
                              }));
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div
                                  className={`w-2 h-2 rounded-full mr-3 ${
                                    selectedWarehouse?.id === warehouse.id
                                      ? "bg-blue-400"
                                      : "bg-gray-500"
                                  }`}
                                ></div>
                                <span className="truncate">
                                  {warehouse.name}
                                </span>
                              </div>
                              {selectedWarehouse?.id === warehouse.id && (
                                <svg
                                  className="w-4 h-4 text-blue-400"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>

                      {/* Footer with count */}
                      {warehouses.length > 0 && (
                        <div
                          className="
              px-4 py-2
              border-t border-gray-700/50
              bg-gray-750
              text-xs text-gray-400
            "
                        >
                          {warehouses.length} warehouses available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="relative w-full sm:w-auto lg:w-48 z-[20]">
              <button
                type="button"
                className="
      flex items-center justify-between 
      w-full px-4 py-3
      bg-gray-800
      border border-gray-600
      rounded-lg
      text-gray-100
      text-sm
      shadow-sm
      transition-all
      duration-200
      ease-in-out
      hover:bg-gray-750
      hover:border-gray-500
      focus:outline-none
      focus:ring-2
      focus:ring-blue-500
      focus:border-blue-500
    "
                onClick={() => toggleDropdown("classification")}
              >
                <span className="truncate">
                  {(selectedClassification.length > 12
                    ? selectedClassification?.slice(0, 12) + "..."
                    : selectedClassification) || t("filters.classification")}
                </span>
                <FaChevronDown
                  className={`ml-2 w-4 h-4 text-gray-400 transition-transform duration-200 ${
                    dropdownOpen.classification ? "rotate-180" : ""
                  }`}
                />
              </button>

              {dropdownOpen.classification && (
                <div
                  className="
      absolute z-10 w-full mt-1
      bg-gray-800
      border border-gray-600
      rounded-lg
      shadow-lg
      max-h-96
      overflow-y-auto
      backdrop-blur-sm
    "
                >
                  <ul className="py-1">
                    {/* Default/Clear option */}
                    <li
                      className="
            px-4 py-2
            text-sm text-gray-100
            hover:bg-gray-700
            hover:text-white
            cursor-pointer
            transition-colors
            duration-150
            ease-in-out
          "
                      onClick={() => {
                        setSelectedClassification("");
                        setDropdownOpen((prev) => ({
                          ...prev,
                          classification: false,
                        }));
                      }}
                    >
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full mr-3 bg-gray-500"></div>
                        {t("filters.classification")}
                      </div>
                    </li>

                    {/* Classification options */}
                    {classificationOptions.map((option, index) => (
                      <li
                        key={index}
                        className="
              px-4 py-2
              text-sm text-gray-100
              hover:bg-gray-700
              hover:text-white
              cursor-pointer
              transition-colors
              duration-150
              ease-in-out
              border-t border-gray-700/50
            "
                        onClick={() => {
                          setSelectedClassification(option.value);
                          setDropdownOpen((prev) => ({
                            ...prev,
                            classification: false,
                          }));
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div
                              className={`w-2 h-2 rounded-full mr-3 ${
                                selectedClassification === option.value
                                  ? "bg-blue-400"
                                  : "bg-gray-500"
                              }`}
                            ></div>
                            <span className="truncate">{option.value}</span>
                          </div>
                          {selectedClassification === option.value && (
                            <svg
                              className="w-4 h-4 text-blue-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>

                  {/* Optional: Footer with count */}
                  {classificationOptions.length > 0 && (
                    <div
                      className="
          px-4 py-2
          border-t border-gray-700/50
          bg-gray-750
          text-xs text-gray-400
        "
                    >
                      {classificationOptions.length} options available
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="text-sm text-gray-400 text-center sm:text-left">
              <span className="text-sm text-gray-400">
                {t("utilizationBoard.contractsActive", {
                  count: visibleContracts.length,
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Gantt Chart Container */}
      <div className="flex relative items-start min-w-0">
        {/* Left Panel - Asset Names (inline on all breakpoints for row alignment) */}
        <div
          className={`
          flex-shrink-0 overflow-hidden bg-gray-800 border-r border-gray-700
          transition-[width] duration-300 ease-in-out
          ${sidebarOpen ? "w-28 sm:w-36 md:w-44" : "w-0 border-r-0"}
          lg:w-64
        `}
        >
          <div className="border-b border-gray-700 sticky top-0 z-10 bg-gray-800">
            <div
              className={`${GANTT_HEADER_MONTH_H} border-b border-gray-600 px-2 sm:px-3 flex items-center shrink-0`}
            >
              <h3 className="text-xs sm:text-sm font-medium text-gray-300 truncate">
                {t("utilizationBoard.model")}
              </h3>
            </div>
            <div
              className={`border-b border-gray-600 ${GANTT_HEADER_DAY_ABBR_H} shrink-0`}
            />
            <div className={`${GANTT_HEADER_DAY_NUM_H} shrink-0`} />
          </div>
          <div>
            {assets.map((asset) => (
              <div
                key={asset.id}
                className={`${GANTT_ROW_H} box-border border-b border-gray-700 hover:bg-gray-750 transition-colors cursor-pointer flex items-center px-1.5 sm:px-2.5 overflow-hidden`}
                onClick={() => {
                  navigate(`/asset-detail-view/${asset?.id}`);
                }}
              >
                <div className="flex gap-1 sm:gap-1.5 items-center min-w-0 w-full overflow-hidden">
                  <img
                    className="h-6 w-6 sm:h-7 sm:w-7 shrink-0 object-cover overflow-hidden rounded"
                    src={getAttachmentThumbUrl(asset?.attachments?.[0]) || assetImages[0]}
                    alt="asset"
                  />
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div
                      className="font-medium text-[10px] sm:text-xs leading-tight overflow-hidden whitespace-nowrap"
                      title={asset?.manufacturer || "asset name"}
                    >
                      {asset?.manufacturer || "asset name"}
                    </div>
                    <div
                      className="text-[10px] sm:text-[11px] text-gray-400 leading-tight mt-0.5 overflow-hidden whitespace-nowrap"
                      title={asset.type || "Forklift"}
                    >
                      {asset.type || "Forklift"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Timeline */}
        <div className="flex-1 min-w-0 overflow-x-auto relative" ref={timelineRef}>
          {/* Current Day Marker */}
          {currentDayPosition !== null && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 pointer-events-none"
              style={{
                left: `${currentDayPosition}px`,
              }}
            >
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-3 h-3 bg-red-500 rounded-full"></div>
            </div>
          )}

          {/* Timeline Header */}
          <div className="border-b border-gray-700 sticky top-0 z-10 bg-gray-900">
            {/* Month Headers */}
            <div
              className="flex border-b border-gray-600"
              style={{
                minWidth: `${
                  timeline.reduce((acc, month) => acc + month.daysInMonth, 0) *
                  24
                }px`,
              }}
            >
              {timeline.map((month, index) => (
                <div
                  key={index}
                  className={`border-r border-gray-600 text-center ${GANTT_HEADER_MONTH_H} flex items-center justify-center flex-shrink-0`}
                  style={{ width: `${month.daysInMonth * 24}px` }}
                >
                  <div className="text-sm font-medium">{month.shortName}</div>
                </div>
              ))}
            </div>

            {/* Day Abbreviations Headers */}
            <div
              className={`flex border-b border-gray-600 ${GANTT_HEADER_DAY_ABBR_H}`}
              style={{
                minWidth: `${
                  timeline.reduce((acc, month) => acc + month.daysInMonth, 0) *
                  24
                }px`,
              }}
            >
              {timeline.map((month, monthIndex) => (
                <div key={monthIndex} className="flex">
                  {month.days.map((day, dayIndex) => (
                    <div
                      key={`${monthIndex}-${dayIndex}`}
                      className={`w-6 ${GANTT_HEADER_DAY_ABBR_H} border-r border-gray-600 text-center text-xs flex-shrink-0 flex items-center justify-center ${
                        day.isWeekend
                          ? "bg-red-900 text-red-300"
                          : "bg-gray-800 text-gray-300"
                      }`}
                      style={{ minWidth: "24px", width: "24px" }}
                    >
                      {day.abbreviation}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Day Numbers Headers */}
            <div
              className={`flex ${GANTT_HEADER_DAY_NUM_H}`}
              style={{
                minWidth: `${
                  timeline.reduce((acc, month) => acc + month.daysInMonth, 0) *
                  24
                }px`,
              }}
            >
              {timeline.map((month, monthIndex) => (
                <div key={monthIndex} className="flex">
                  {month.days.map((day, dayIndex) => (
                    <div
                      key={`${monthIndex}-${dayIndex}`}
                      className={`w-6 ${GANTT_HEADER_DAY_NUM_H} border-r border-gray-600 text-center text-xs flex items-center justify-center flex-shrink-0 ${
                        day.isWeekend
                          ? "bg-red-900 text-red-300"
                          : "bg-gray-800 text-gray-300"
                      }`}
                      style={{ minWidth: "24px", width: "24px" }}
                    >
                      {day.number}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Contract Bars - Map over assets instead of contracts */}
          <div className="relative">
            {assets.map((asset, assetIndex) => {
              const assetContracts = getAssetVisibleContracts(asset);
              const showWarehouse = shouldShowWarehouseStatus(asset);

              return (
                <div
                  key={asset.id}
                  className={`${GANTT_ROW_H} box-border border-b border-gray-700 hover:bg-gray-750 transition-colors relative`}
                  style={{
                    minWidth: `${
                      timeline.reduce(
                        (acc, month) => acc + month.daysInMonth,
                        0
                      ) * 24
                    }px`,
                  }}
                >
                  {/* Weekend background stripes for contract rows */}
                  <div className="absolute inset-0 flex">
                    {timeline.map((month, monthIndex) => (
                      <div key={monthIndex} className="flex">
                        {month.days.map((day, dayIndex) => (
                          <div
                            key={`${monthIndex}-${dayIndex}`}
                            className={`w-6 h-full ${
                              day.isWeekend ? " bg-opacity-10" : ""
                            }`}
                            style={{ minWidth: "24px", width: "24px" }}
                          />
                        ))}
                      </div>
                    ))}
                  </div>

                  {assetContracts.length > 0
                    ? assetContracts.map((contract, contractIndex) => {
                        const { left, width, visible } =
                          calculateContractBar(contract);

                        if (!visible) return null;

                        return (
                          <div
                            key={`${asset.id}-${contract.contract_number}`}
                            className={`absolute cursor-pointer transition-all duration-200 hover:opacity-80 flex items-center px-2 rounded ${getContractColor(
                              contract.contract_type,
                              contractIndex
                            )}`}
                            style={{
                              left: `${left}px`,
                              width: `${Math.max(width, 80)}px`,
                              minWidth: "80px",
                              top: `${4 + contractIndex * 2}px`,
                              height: `28px`,
                              zIndex: assetContracts.length - contractIndex,
                            }}
                            onClick={() => openContractModal(contract)}
                          >
                            <p className="text-xs w-full flex items-center justify-center font-medium text-white truncate">
                              <span className="capitalize">
                                {contract.contract_type} {" , "}{" "}
                                {contract.invoice_address_city}
                              </span>
                            </p>
                          </div>
                        );
                      })
                    : showWarehouse
                    ? (() => {
                        const { startDate, endDate } =
                          getWarehouseContractDates(asset);
                        const { left, width, visible } = calculateContractBar({
                          contract_start: startDate,
                          contract_end: endDate,
                        });

                        if (!visible) return null;

                        return (
                          <div
                            className={`absolute top-1 h-8 rounded cursor-pointer transition-all duration-200 hover:opacity-80 flex items-center px-2 ${getContractColor(
                              "In Warehouse"
                            )}`}
                            style={{
                              left: `${left}px`,
                              width: `${Math.max(width, 120)}px`,
                              minWidth: "120px",
                            }}
                            onClick={() => {
                              // Create a dummy contract for "In Warehouse" display
                              const dummyContract = {
                                contract_number: `warehouse-${asset.id}`,
                                asset: asset.id,
                                contract_start: startDate,
                                contract_end: "",
                                contract_price_euro: "0.00",
                                contract_type: "In Warehouse",
                                created_at: new Date().toISOString(),
                                updated_at: new Date().toISOString(),
                                customer: {
                                  name: "",
                                  email: "",
                                  phone_number: "",
                                  customer_company: "",
                                  customer_manager: null,
                                  customer_number: "",
                                },
                                invoice_address_street: "Lessingstr. 14",
                                invoice_address_zip: "46149",
                                invoice_address_city: "Oberhausen",
                                invoice_address_country: "Germany",
                              };
                              openContractModal(dummyContract);
                            }}
                          >
                            <p className="text-xs w-full flex items-center justify-center font-medium text-white truncate">
                              <span>
                                {t("contract.inWarehouse") + " (Oberhausen)"}{" "}
                              </span>
                            </p>
                          </div>
                        );
                      })()
                    : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contract Details Modal */}
      {selectedContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold capitalize">
                {selectedContract?.contract_type}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 hover:bg-gray-700 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Timeline */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Calendar size={16} className="text-blue-400" />
                    <span className="text-sm font-medium">
                      {t("contractModal.timeline")}
                    </span>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-sm text-gray-300">
                      {t("contractModal.startDate")}
                    </div>
                    <div className="font-medium">
                      {new Date(
                        selectedContract.contract_start
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                    <div className="text-sm text-gray-300 mt-2">
                      {t("contractModal.endDate")}
                    </div>
                    <div className="font-medium">
                      {selectedContract?.contract_end
                        ? new Date(
                            selectedContract.contract_end
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "N/A"}
                    </div>
                  </div>
                </div>

                {/* Contract Details */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <FileText size={16} className="text-green-400" />
                    <span className="text-sm font-medium">
                      {t("contractModal.contractDetails")}
                    </span>
                  </div>
                  <div className="bg-gray-700 p-4 rounded-lg">
                    <div className="text-sm text-gray-300">
                      {t("contractModal.type")}
                    </div>
                    <div className="font-medium capitalize">
                      {selectedContract?.contract_type}
                    </div>
                    {selectedContract?.contract_price_euro &&
                      selectedContract.contract_type !== "In Warehouse" && (
                        <>
                          <div className="text-sm text-gray-300 mt-2">
                            {t("contractModal.price")}
                          </div>
                          <div className="font-medium">
                            €{selectedContract?.contract_price_euro}
                          </div>
                        </>
                      )}
                  </div>
                </div>

                {selectedContract?.customer &&
                  selectedContract.contract_type == "In Warehouse" && (
                    <div className="md:col-span-2 space-y-4">
                      <div className="bg-gray-700 p-4 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {selectedContract.customer?.name && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("customer.name")}
                            </div>
                            <div className="font-medium">
                              {selectedContract.customer?.name}
                            </div>
                          </div>
                        )}
                        {selectedContract.customer?.email && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("customer.email")}
                            </div>
                            <div className="font-medium">
                              {selectedContract.customer?.email}
                            </div>
                          </div>
                        )}
                        {selectedContract.customer?.phone_number && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("customer.phone")}
                            </div>
                            <div className="font-medium">
                              {selectedContract.customer?.phone_number}
                            </div>
                          </div>
                        )}
                        {selectedContract?.customer?.customer_company && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("customer.company")}
                            </div>
                            <div className="font-medium">
                              {selectedContract.customer?.customer_company}
                            </div>
                          </div>
                        )}

                        {selectedContract?.invoice_address_street && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("contractModal.warehouseAddress")}
                            </div>
                            <div className="font-medium">
                              {selectedContract?.invoice_address_street},<br />
                              {selectedContract?.invoice_address_city} -{" "}
                              {selectedContract?.invoice_address_zip},<br />
                              {selectedContract?.invoice_address_country}
                            </div>
                          </div>
                        )}
                        {selectedContract?.delivery_address_city && (
                          <div>
                            <div className="text-sm text-gray-300">
                              Delivery Address
                            </div>
                            <div className="font-medium">
                              {selectedContract?.delivery_address_street},<br />
                              {selectedContract?.delivery_address_city} -{" "}
                              {selectedContract?.delivery_address_zip},<br />
                              {selectedContract?.delivery_address_country}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* Customer Info */}
                {selectedContract?.customer &&
                  selectedContract.contract_type !== "In Warehouse" && (
                    <div className="md:col-span-2 space-y-4">
                      <div className="flex items-center space-x-2">
                        <User size={16} className="text-yellow-400" />
                        <span className="text-sm font-medium">
                          {t("contractModal.customerInfo")}
                        </span>
                      </div>
                      <div className="bg-gray-700 p-4 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {selectedContract.customer?.name && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("customer.name")}
                            </div>
                            <div className="font-medium">
                              {selectedContract.customer?.name}
                            </div>
                          </div>
                        )}
                        {selectedContract.customer?.email && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("customer.email")}
                            </div>
                            <div className="font-medium">
                              {selectedContract.customer?.email}
                            </div>
                          </div>
                        )}
                        {selectedContract.customer?.phone_number && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("customer.phone")}
                            </div>
                            <div className="font-medium">
                              {selectedContract.customer?.phone_number}
                            </div>
                          </div>
                        )}
                        {selectedContract?.customer?.customer_company && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("customer.company")}
                            </div>
                            <div className="font-medium">
                              {selectedContract.customer?.customer_company}
                            </div>
                          </div>
                        )}

                        {selectedContract?.invoice_address_street && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("contractModal.invoiceAddress")}
                            </div>
                            <div className="font-medium">
                              {selectedContract?.invoice_address_street},<br />
                              {selectedContract?.invoice_address_city} -{" "}
                              {selectedContract?.invoice_address_zip},<br />
                              {selectedContract?.invoice_address_country}
                            </div>
                          </div>
                        )}
                        {selectedContract?.delivery_address_city && (
                          <div>
                            <div className="text-sm text-gray-300">
                              {t("contractModal.deliveryAddress")}
                            </div>
                            <div className="font-medium">
                              {selectedContract?.delivery_address_street},<br />
                              {selectedContract?.delivery_address_city} -{" "}
                              {selectedContract?.delivery_address_zip},<br />
                              {selectedContract?.delivery_address_country}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttChart;
